const LEGACY_PERMISSION_EXPANSIONS: Record<string, string[]> = {
    'properties:write': ['properties:read', 'properties:create', 'properties:edit', 'properties:delete'],
    'properties:edit': [
        'properties:read',
        'payment_plans:read',
        'payment_plans:create',
        'payment_plans:edit',
        'payment_plans:delete',
        'payment_plans:assign',
        'properties.assignments:write'
    ],
    'files:read': ['media.library:read'],
    'files:write': [
        'files:read',
        'media.library:read',
        'media.library:create',
        'media.library:edit',
        'media.library:delete'
    ],
    'files:publish': ['files:read', 'media.library:read', 'media.library:edit'],
    'media.library:create': ['media.library:read'],
    'media.library:edit': ['media.library:read'],
    'media.library:delete': ['media.library:read'],
    'payment_plans:create': ['payment_plans:read'],
    'payment_plans:edit': ['payment_plans:read'],
    'payment_plans:delete': ['payment_plans:read'],
    'payment_plans:assign': ['payment_plans:read'],
    'offers:read': ['offers.settings:read', 'offers.unit_defaults:read', 'offers.drafts:read'],
    'offers:write': [
        'offers:read',
        'offers.settings:read',
        'offers.settings:write',
        'offers.unit_defaults:read',
        'offers.unit_defaults:write',
        'offers.drafts:read',
        'offers.drafts:write'
    ],
    'offers:send': ['offers:read', 'offers.drafts:read'],
    'offers.settings:read': ['offers:read'],
    'offers.unit_defaults:read': ['offers:read'],
    'offers.drafts:read': ['offers:read'],
    'offers.settings:write': ['offers.settings:read', 'offers:read', 'offers:write'],
    'offers.unit_defaults:write': ['offers.unit_defaults:read', 'offers:read', 'offers:write'],
    'offers.drafts:write': ['offers.drafts:read', 'offers:read', 'offers:write'],
    'portfolio:read': ['workspace.portfolio:read', 'portfolio.commission.self:read'],
    'workspace.portfolio:read': ['portfolio.commission.self:read'],
    'portfolio.commission:read': ['portfolio.commission.self:read'],
    'properties.assignments:write': ['properties:read'],
    // NOTE: `leads.assignments:write` deliberately expands to NOTHING.
    //
    // It used to expand to `reservations:read`, mirroring a server-side
    // derivation in effectivePermissions.js. That derivation was REMOVED because
    // `reservations:read` is the "Lead visibility" toggle, and migration 151
    // auto-granted `leads.assignments:write` to every membership already holding
    // `properties.assignments:write` — so switching Lead visibility off did
    // nothing for a large set of members.
    //
    // This map MUST stay in sync with the server's DERIVED_PERMISSIONS. If the
    // client expands a code the server does not, the user sees nav items and
    // screens they are then 403'd from on every request — worse than not seeing
    // them at all. The assign-leads screen is unaffected: it is gated on
    // `leads.assignments:write` itself and calls `/lead-assignments/*`, which
    // does not require lead visibility.
    'leads.assignments:write': []
};

// Legacy `users:*` codes are internal aliases — NOT directly grantable.
// They are hidden from the permission UI (same as the backend HIDDEN_CODES set).
// The silent expansion (users:write → partners:* + sales_team:* + ...) has been REMOVED
// to fix the "allow one, get three" bug (3g). Permission check functions still check
// both `users:*` and the granular codes, so existing users with `users:*` are unaffected.
// See 05-STABILIZATION-PLAN.md P1.2 (D1, D14).
export const LEGACY_HIDDEN_PERMISSION_CODES = new Set([
    'properties:write',
    'users:read',
    'users:create',
    'users:edit',
    'users:delete',
    'users:write'
]);

export function isHiddenPermissionCode(code: string): boolean {
    return LEGACY_HIDDEN_PERMISSION_CODES.has(String(code || '').trim().toLowerCase());
}

export function normalizePermissionCodes(raw: unknown): string[] {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const normalized: string[] = [];
    const seen = new Set<string>();

    const push = (value: unknown): string | null => {
        const code = String(value || '').trim().toLowerCase();
        if (!code || seen.has(code)) return null;
        seen.add(code);
        normalized.push(code);
        return code;
    };

    for (const item of list) {
        const queue = [String(item || '').trim().toLowerCase()].filter(Boolean);
        while (queue.length) {
            const code = push(queue.shift());
            if (!code) continue;
            const expanded = LEGACY_PERMISSION_EXPANSIONS[code];
            if (expanded?.length) {
                expanded.forEach((next) => {
                    const normalizedCode = String(next || '').trim().toLowerCase();
                    if (normalizedCode && !seen.has(normalizedCode)) queue.push(normalizedCode);
                });
            }
        }
    }

    return normalized;
}

export function permissionSetFromRaw(raw: unknown): Set<string> {
    return new Set(normalizePermissionCodes(raw));
}

export function permissionSetFromCodes(codes: Iterable<unknown>): Set<string> {
    return permissionSetFromRaw(Array.from(codes || []));
}

export function hasPropertyReadPermission(perms: Set<string>): boolean {
    return (
        perms.has('properties:read')
        || hasPropertyCreatePermission(perms)
        || hasPropertyEditPermission(perms)
        || hasPropertyDeletePermission(perms)
    );
}

export function hasLeadAssignmentPermission(perms: Set<string>): boolean {
    return perms.has('leads.assignments:write');
}

export function hasPropertyCreatePermission(perms: Set<string>): boolean {
    return perms.has('properties:create') || perms.has('properties:write');
}

export function hasPropertyEditPermission(perms: Set<string>): boolean {
    return perms.has('properties:edit') || perms.has('properties:write');
}

export function hasPropertyDeletePermission(perms: Set<string>): boolean {
    return perms.has('properties:delete') || perms.has('properties:write');
}

export function hasPropertyStatusPermission(perms: Set<string>): boolean {
    return perms.has('properties.fields.status:edit') || perms.has('properties:approve');
}

export function hasOfferReadPermission(perms: Set<string>): boolean {
    return perms.has('offers:read')
        || perms.has('offers:write')
        || perms.has('offers:send')
        || perms.has('offers.settings:read')
        || perms.has('offers.settings:write')
        || perms.has('offers.unit_defaults:read')
        || perms.has('offers.unit_defaults:write')
        || perms.has('offers.drafts:read')
        || perms.has('offers.drafts:write');
}

export function hasMediaLibraryReadPermission(perms: Set<string>): boolean {
    return perms.has('media.library:read')
        || perms.has('media.library:create')
        || perms.has('media.library:edit')
        || perms.has('media.library:delete')
        || perms.has('files:read')
        || perms.has('files:write')
        || perms.has('files:publish');
}

export function hasMediaLibraryCreatePermission(perms: Set<string>): boolean {
    return perms.has('media.library:create') || perms.has('files:write');
}

export function hasMediaLibraryEditPermission(perms: Set<string>): boolean {
    return perms.has('media.library:edit') || perms.has('files:write') || perms.has('files:publish');
}

export function hasMediaLibraryDeletePermission(perms: Set<string>): boolean {
    return perms.has('media.library:delete') || perms.has('files:write');
}

export function hasPaymentPlanReadPermission(perms: Set<string>): boolean {
    return perms.has('payment_plans:read')
        || perms.has('payment_plans:create')
        || perms.has('payment_plans:edit')
        || perms.has('payment_plans:delete')
        || perms.has('payment_plans:assign')
        || hasPropertyEditPermission(perms);
}

export function hasPaymentPlanCreatePermission(perms: Set<string>): boolean {
    return perms.has('payment_plans:create') || hasPropertyEditPermission(perms);
}

export function hasPaymentPlanEditPermission(perms: Set<string>): boolean {
    return perms.has('payment_plans:edit') || hasPropertyEditPermission(perms);
}

export function hasPaymentPlanDeletePermission(perms: Set<string>): boolean {
    return perms.has('payment_plans:delete') || hasPropertyEditPermission(perms);
}

export function hasPaymentPlanAssignPermission(perms: Set<string>): boolean {
    return perms.has('payment_plans:assign') || hasPropertyEditPermission(perms);
}

export function haspartnerReadPermission(perms: Set<string>): boolean {
    // partners:{read,create,edit,delete} was de-duplicated into subpartners:* (2026-06-30);
    // the developer Partners page + partner-org relationship routes now gate on subpartners:*.
    return perms.has('subpartners:read')
        || perms.has('users:read')
        || perms.has('users:write');
}

export function haspartnerCreatePermission(perms: Set<string>): boolean {
    return perms.has('subpartners:create') || perms.has('users:create') || perms.has('users:write');
}

export function haspartnerEditPermission(perms: Set<string>): boolean {
    return perms.has('subpartners:edit') || perms.has('users:edit') || perms.has('users:write');
}

export function haspartnerDeletePermission(perms: Set<string>): boolean {
    return perms.has('subpartners:delete') || perms.has('users:delete') || perms.has('users:write');
}

export function haspartnerWorkspaceAccessPermission(perms: Set<string>): boolean {
    return haspartnerReadPermission(perms)
        || haspartnerCreatePermission(perms)
        || haspartnerEditPermission(perms)
        || haspartnerDeletePermission(perms);
}

export function hasPropertyAssignmentPermission(perms: Set<string>): boolean {
    return perms.has('properties.assignments:write') || perms.has('users:edit') || perms.has('users:write');
}

export function hasSalesTeamReadPermission(perms: Set<string>): boolean {
    return perms.has('sales_team:read')
        || perms.has('users:read')
        || perms.has('users:write');
}

export function hasSalesTeamCreatePermission(perms: Set<string>): boolean {
    return perms.has('sales_team:create') || perms.has('users:create') || perms.has('users:write');
}

export function hasSalesTeamEditPermission(perms: Set<string>): boolean {
    return perms.has('sales_team:edit') || perms.has('users:edit') || perms.has('users:write');
}

export function hasSalesTeamDeletePermission(perms: Set<string>): boolean {
    return perms.has('sales_team:delete') || perms.has('users:delete') || perms.has('users:write');
}

export function hasSalesTeamWorkspaceAccessPermission(perms: Set<string>): boolean {
    return hasSalesTeamReadPermission(perms)
        || hasSalesTeamCreatePermission(perms)
        || hasSalesTeamEditPermission(perms)
        || hasSalesTeamDeletePermission(perms);
}

export function hasPartnerWorkspacePermissions(perms: Set<string>): boolean {
    return (
        perms.has('projects:read')
        || perms.has('projects:write')
        || hasPropertyReadPermission(perms)
        || hasPropertyStatusPermission(perms)
        || perms.has('reservations:read')
        || perms.has('reservations:write')
        || perms.has('reservations:reserve_for_client')
        || perms.has('users:write')
        || perms.has('users:read')
        || perms.has('users:create')
        || perms.has('users:edit')
        || perms.has('users:delete')
        || hasOfferReadPermission(perms)
        || hasMediaLibraryReadPermission(perms)
        || hasPaymentPlanReadPermission(perms)
        || perms.has('org.settings:read')
        || perms.has('org.settings:write')
        || perms.has('portfolio:read')
        || perms.has('portfolio.total:read')
        || perms.has('portfolio.commission.self:read')
        || perms.has('portfolio.subpartners:read')
        || perms.has('portfolio.sales:read')
        || perms.has('workspace.portfolio:read')
        || perms.has('workspace.commission:read')
        || hasPropertyAssignmentPermission(perms)
        || haspartnerWorkspaceAccessPermission(perms)
        || hasSalesTeamWorkspaceAccessPermission(perms)
    );
}

export function canViewPortfolioCommission(perms: Set<string>): boolean {
    return perms.has('portfolio.commission:read') || perms.has('workspace.commission:read') || perms.has('portfolio.commission.self:read');
}

export function hasPostSaleFollowUpPermission(perms: Set<string>): boolean {
    return perms.has('post_sale:read');
}

// ─── Post-sale kill-switch filtering ─────────────────────────────────────────
//
// When POST_SALE_FOLLOWUP_ENABLED is false, all post_sale:* and documents.*
// permission codes must be hidden from every UI surface (partner-grant dialogs,
// role presets, permission groups). See zcode/business-spec/06-POST-SALE-HIDING-SPEC.md.

const POST_SALE_CODE_PREFIXES = ['post_sale:', 'post_sale.', 'documents.', 'documents:'];

export function isPostSalePermissionCode(code: string): boolean {
    const normalized = String(code || '').trim().toLowerCase();
    if (!normalized) return false;
    return POST_SALE_CODE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export function filterPostSalePermissionCodes(codes: readonly string[], postSaleEnabled: boolean): string[] {
    if (postSaleEnabled) return [...codes];
    return codes.filter((code) => !isPostSalePermissionCode(code));
}

export function filterPostSalePermissions<T extends { code: string }>(items: readonly T[], postSaleEnabled: boolean): T[] {
    if (postSaleEnabled) return [...items];
    return items.filter((item) => !isPostSalePermissionCode(item.code));
}
